import React, { useState, forwardRef, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  Platform,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { parseInternationalPhoneNumber } from '../../utils/phoneNumber';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { typography } from '../../theme/typography';
import { designTokens } from '../../theme/design-system';
import { useAppStore } from '../../store/appStore';
import { useLocale } from '../../contexts/LocaleContext';
import { useTypography } from '../../hooks/useTypography';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { defaultPhoneCountry } from '../../utils/businessDefaults';

export interface PhoneInputProps {
  value?: string;
  onChangeText?: (text: string) => void;
  onChange?: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'filled';
  state?: 'default' | 'error' | 'success' | 'warning';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  style?: any;
  containerStyle?: any;
  initialCountry?: string;  // ISO country code (e.g., 'EG', 'SA', 'US')
  mode?: 'edit' | 'view' | 'admin';  // Controls editability: edit/admin = editable, view = read-only
  // Add a ref to access the component methods
  ref?: React.RefObject<{ 
    getValue: () => {
      formattedNumber: string;
      dialCode: string;
      countryCode: string;
      nationalNumber: string;
      country: string;
      e164Format: string;
      internationalFormat: string;
    }; 
    getDialCode: () => string;
    getCountryCode: () => string;
    setCountry: (countryCode: string) => void;
    inputRef: React.RefObject<TextInput> 
  }>;
}

export const PhoneInputComponent = forwardRef<{ 
  getValue: () => {
    formattedNumber: string;
    dialCode: string;
    countryCode: string;
    nationalNumber: string;
    country: string;
  }; 
  getDialCode: () => string;
  getCountryCode: () => string;
  setCountry: (countryCode: string) => void;
  inputRef: React.RefObject<TextInput> 
}, PhoneInputProps>(({
  value = '', onChangeText, onChange, label, placeholder = 'Enter phone number',
  error, helperText, disabled = false, required = false, size = 'md',
  variant = 'default', state = 'default', leftIcon, rightIcon, style, containerStyle,
  initialCountry, mode = 'edit',
}, ref) => {
  const styles = useThemedStyles(getStyles);
  const [isCountryPickerOpen, setIsCountryPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(initialCountry || defaultPhoneCountry());
  const inputRef = useRef<TextInput>(null);
  const { isRTL } = useLocale();
  const typo = useTypography();
  
  // Sync selectedCountry with initialCountry prop when it changes
  // This is important when importing contacts with different countries
  useEffect(() => {
    if (initialCountry && initialCountry !== selectedCountry) {
      console.log('📞 [PhoneInput] initialCountry changed, updating selectedCountry:', initialCountry);
      setSelectedCountry(initialCountry);
    }
  }, [initialCountry]);
  
  // Get countries from centralized store
  const { countries } = useAppStore();
  
  // Determine if input should be editable based on mode
  const isViewMode = mode === 'view';
  const isEditable = !disabled && !isViewMode && (mode === 'edit' || mode === 'admin');
  
  // Format phone number for display in view mode
  const getDisplayValue = () => {
    if (isViewMode) {
      if (!value || value.trim() === '') {
        return 'N/A';
      }
      // Try to format the phone number if possible
      try {
        const parsed = parseInternationalPhoneNumber(value);
        if (parsed) {
          return parsed.internationalFormat || parsed.e164Format || value;
        }
      } catch (e) {
        // If parsing fails, just return the value
      }
      return value;
    }
    return value;
  };
  
  // Expose methods and inputRef through ref
  React.useImperativeHandle(ref, () => ({
    getValue: () => {
      // Get ISO country code (e.g., 'EG', 'US', 'SA')
      const isoCountryCode = selectedCountry || defaultPhoneCountry();
      // Get dial code with + (e.g., '+20', '+1', '+966')
      const dialCode = selectedCountryData?.dialCode || '';
      // Get country name
      const country = selectedCountryData?.name || '';
      
      // Clean the phone number (remove all non-digit characters)
      const nationalNumber = value.replace(/\D/g, '');
      
      // Combine dial code + phone number for formattedNumber
      const formattedNumber = dialCode ? `${dialCode}${nationalNumber}` : `+${nationalNumber}`;
      
      // Try to parse for better formatting
      let e164Format = formattedNumber;
      let internationalFormat = formattedNumber;
      try {
        const parsed = parseInternationalPhoneNumber(formattedNumber);
        if (parsed) {
          e164Format = parsed.e164Format || formattedNumber;
          internationalFormat = parsed.internationalFormat || formattedNumber;
        }
      } catch (e) {
        // Use formattedNumber as fallback
      }
      
      const result = {
        formattedNumber,
        dialCode,
        countryCode: isoCountryCode, // ISO country code like 'EG', 'US', 'SA'
        country: isoCountryCode, // Keep for backward compatibility
        nationalNumber,
        e164Format,
        internationalFormat
      };
      
      console.log('🔍 [PhoneInput] getValue() called:', {
        selectedCountry: selectedCountry,
        result: result
      });
      
      return result;
    },
    getDialCode: () => {
      // Return the dial code with + symbol (e.g., "+20", "+1", "+44")
      const dialCode = selectedCountryData?.dialCode || '';
      
      console.log('🔍 [PhoneInput] getDialCode() called:', {
        selectedCountry: selectedCountry,
        dialCode: dialCode
      });
      
      return dialCode;
    },
    getCountryCode: () => {
      // Return the country code without + symbol (e.g., "20", "1", "44")
      const countryCode = selectedCountryData?.dialCode?.replace('+', '') || '';
      
      console.log('🔍 [PhoneInput] getCountryCode() called:', {
        selectedCountry: selectedCountry,
        countryCode: countryCode
      });
      
      return countryCode;
    },
    setCountry: (countryCode: string) => {
      console.log('🔍 [PhoneInput] setCountry() called with:', countryCode);
      setSelectedCountry(countryCode);
    },
    inputRef: inputRef as React.RefObject<TextInput>
  }));
  
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.dialCode.includes(searchQuery)
  );

  const selectedCountryData = countries.find(c => c.code === selectedCountry);

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return { height: 40, fontSize: 14, paddingHorizontal: 12 };
      case 'lg':
        return { height: 56, fontSize: 18, paddingHorizontal: 16 };
      default: // md
        return { height: 48, fontSize: 16, paddingHorizontal: 14 };
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'outlined':
        return {
          borderWidth: 1,
          borderColor: state === 'error' ? colors.error : colors.border,
          backgroundColor: colors.background.surface,
        };
      case 'filled':
        return {
          borderWidth: 0,
          backgroundColor: colors.lightGray,
        };
      default:
        return {
          borderWidth: 1,
          borderColor: state === 'error' ? colors.error : colors.border,
          backgroundColor: colors.background.surface,
        };
    }
  };

  const getStateStyles = () => {
    switch (state) {
      case 'error':
        return { borderColor: colors.error };
      case 'success':
        return { borderColor: colors.success };
      case 'warning':
        return { borderColor: colors.warning };
      default:
        return {};
    }
  };

  const sizeStyles = getSizeStyles();
  const variantStyles = getVariantStyles();
  const stateStyles = getStateStyles();

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setIsCountryPickerOpen(false);
    setSearchQuery('');
    
    // Don't clear the phone input when country changes
    // Users should be able to change country without losing their phone number
  };

  const handlePhoneChange = (phoneValue: string) => {
    // Use centralized phone number parsing utility (same as WhatsAppContactModal)
    // This handles country code conflicts correctly (e.g., +20 vs +201, +966 vs +965)
    let nationalNumber = phoneValue;
    let detectedCountry = selectedCountry;
    let shouldExtractNationalNumber = false;
    
    // Only try to parse if we have enough digits (at least 7 for a complete number)
    if (phoneValue.trim().length >= 7) {
      try {
        // Try parsing as-is first (works for numbers with + prefix)
        let phoneToParse = phoneValue.trim();
        
        // If phone doesn't start with +, try adding it for parsing
        if (!phoneToParse.startsWith('+')) {
          phoneToParse = `+${phoneToParse}`;
        }
        
        // Use centralized parsing utility (same logic as WhatsAppContactModal)
        const parsed = parseInternationalPhoneNumber(phoneToParse);
        
        if (parsed && parsed.isValid) {
          // Successfully parsed - extract national number and country
          nationalNumber = parsed.nationalNumber;
          detectedCountry = parsed.country || selectedCountry;
          shouldExtractNationalNumber = true;
        }
      } catch (error) {
        // Parsing failed, keep original value
        console.log('📞 [PhoneInput] Could not parse phone number, using as-is:', phoneValue);
      }
    }
    
    // Update country if detected
    if (detectedCountry !== selectedCountry && shouldExtractNationalNumber) {
      setSelectedCountry(detectedCountry);
    }
    
    // Determine what value to pass to parent
    // If we extracted a national number, use that; otherwise use the original value
    const valueToPass = shouldExtractNationalNumber && nationalNumber !== phoneValue 
      ? nationalNumber 
      : phoneValue;
    
    // Pass the value to parent (controlled component will update via props, uncontrolled will update internally)
    if (onChange) {
      onChange(valueToPass);
    }
    if (onChangeText) {
      onChangeText(valueToPass);
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      {label && (
        <Text style={[styles.label, { fontFamily: typo.fontFamily.medium, textAlign: isRTL ? 'right' : 'left' }]}>
          {label}
          {required && <Text style={styles.required}> *</Text>}
        </Text>
      )}

      <View style={[styles.inputContainer, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        {leftIcon && (
          <View style={styles.leftIcon}>
            {leftIcon}
          </View>
        )}

        {isViewMode ? (
          /* View Mode: Display phone number as text or N/A */
          <View style={[styles.phoneInput, sizeStyles, variantStyles, stateStyles, style, styles.viewModeContainer]}>
            <Text style={[styles.viewModeText, (!value || value.trim() === '') && styles.viewModePlaceholder]}>
              {getDisplayValue()}
            </Text>
          </View>
        ) : (
          <>
            {/* Country Selector */}
            <TouchableOpacity
              style={[styles.countrySelector, { flexDirection: isRTL ? 'row-reverse' : 'row', marginRight: isRTL ? 0 : 8, marginLeft: isRTL ? 8 : 0 }]}
              onPress={() => setIsCountryPickerOpen(true)}
              disabled={!isEditable}
            >
              <Text style={[styles.flag, { marginRight: isRTL ? 0 : 4, marginLeft: isRTL ? 4 : 0 }]}>{selectedCountryData?.flag}</Text>
              <Text style={[styles.countryCode, { marginRight: isRTL ? 0 : 4, marginLeft: isRTL ? 4 : 0 }]}>{selectedCountryData?.dialCode}</Text>
              <Ionicons name="chevron-down" size={16} color={colors.textDark} />
            </TouchableOpacity>

            {/* Phone Input */}
            <View style={[styles.phoneInput, sizeStyles, variantStyles, stateStyles, style]}>
              <TextInput
                ref={inputRef}
                value={value}
                onChangeText={handlePhoneChange}
                placeholder={placeholder}
                placeholderTextColor={colors.textLight}
                editable={isEditable}
                style={{
                  flex: 1,
                  fontSize: sizeStyles.fontSize,
                  color: colors.textDark,
                  fontFamily: typo.latinFontFamily.primary,
                  fontWeight: '400',
                  textAlign: isRTL ? 'right' : 'left',
                }}
                keyboardType="phone-pad"
                autoComplete="tel"
              />
            </View>
          </>
        )}

        {rightIcon && (
          <View style={styles.rightIcon}>
            {rightIcon}
          </View>
        )}
      </View>

      {/* Error or Helper Text */}
      {(error || helperText) && (
        <Text style={[styles.helperText, error && styles.errorText, { textAlign: isRTL ? 'right' : 'left', fontFamily: typo.fontFamily.primary }]}>
          {error || helperText}
        </Text>
      )}

      {/* Country Picker Modal */}
      <Modal
        visible={isCountryPickerOpen}
        animationType="slide"
        presentationStyle="pageSheet"
        transparent={Platform.OS === 'web'}
        onRequestClose={() => setIsCountryPickerOpen(false)}
      >
        {Platform.OS === 'web' ? (
          <View style={styles.webOverlay}>
            <TouchableOpacity
              style={styles.webBackdrop}
              activeOpacity={1}
              onPress={() => setIsCountryPickerOpen(false)}
            />
            <View style={styles.modalContentConstrained}>
              <View style={styles.modalHeader}>
                <Text style={[styles.modalTitle, { fontFamily: typo.fontFamily.semibold }]}>Select Country</Text>
                <TouchableOpacity
                  onPress={() => setIsCountryPickerOpen(false)}
                  style={styles.closeButton}
                >
                  <Ionicons name="close" size={24} color={colors.textDark} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={styles.searchInput}
                placeholder="Search countries..."
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoFocus
              />

              <FlatList
                data={filteredCountries}
                keyExtractor={(item) => item.code}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[styles.countryItem, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                    onPress={() => handleCountrySelect(item.code)}
                  >
                    <Text style={[styles.countryFlag, { marginRight: isRTL ? 0 : spacing.md, marginLeft: isRTL ? spacing.md : 0 }]}>{item.flag}</Text>
                    <View style={styles.countryInfo}>
                      <Text style={[styles.countryName, { textAlign: isRTL ? 'right' : 'left', fontFamily: typo.fontFamily.medium }]}>{item.name}</Text>
                      <Text style={[styles.countryDialCode, { textAlign: isRTL ? 'right' : 'left', fontFamily: typo.fontFamily.primary }]}>{item.dialCode}</Text>
                    </View>
                  </TouchableOpacity>
                )}
                style={styles.countryList}
                showsVerticalScrollIndicator={false}
              />
            </View>
          </View>
        ) : (
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={[styles.modalTitle, { fontFamily: typo.fontFamily.semibold }]}>Select Country</Text>
                <TouchableOpacity
                  onPress={() => setIsCountryPickerOpen(false)}
                  style={styles.closeButton}
                >
                  <Ionicons name="close" size={24} color={colors.textDark} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={styles.searchInput}
                placeholder="Search countries..."
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoFocus
              />

              <FlatList
                data={filteredCountries}
                keyExtractor={(item) => item.code}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[styles.countryItem, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                    onPress={() => handleCountrySelect(item.code)}
                  >
                    <Text style={[styles.countryFlag, { marginRight: isRTL ? 0 : spacing.md, marginLeft: isRTL ? spacing.md : 0 }]}>{item.flag}</Text>
                    <View style={styles.countryInfo}>
                      <Text style={[styles.countryName, { textAlign: isRTL ? 'right' : 'left', fontFamily: typo.fontFamily.medium }]}>{item.name}</Text>
                      <Text style={[styles.countryDialCode, { textAlign: isRTL ? 'right' : 'left', fontFamily: typo.fontFamily.primary }]}>{item.dialCode}</Text>
                    </View>
                  </TouchableOpacity>
                )}
                style={styles.countryList}
                showsVerticalScrollIndicator={false}
              />
            </View>
          </View>
        )}
      </Modal>
    </View>
  );
});

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: spacing.md,
    width: '100%',
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  required: {
    color: colors.error,
  },
  inputContainer: {
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    flex: 1,
    minWidth: 0,
  },
  leftIcon: {
    position: 'absolute',
    left: 12,
    zIndex: 1,
  },
  rightIcon: {
    position: 'absolute',
    right: 12,
    zIndex: 1,
  },
  countrySelector: {
    alignItems: 'center',
    backgroundColor: colors.lightGray,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    minWidth: 70,
    maxWidth: 80,
  },
  flag: {
    fontSize: 16,
  },
  countryCode: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  phoneInput: {
    flex: 1,
    borderRadius: 8,
    justifyContent: 'center',
    paddingHorizontal: 12,
    minWidth: 0,
    width: '100%',
  },
  viewModeContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  viewModeText: {
    fontSize: 14,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
  viewModePlaceholder: {
    color: colors.textLight,
    fontStyle: 'italic',
  },
  helperText: {
    fontSize: 12,
    color: colors.textLight,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.regular,
  },
  errorText: {
    color: colors.error,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: colors.background.surface,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  modalContent: {
    flex: 1,
  },
  webOverlay: {
    flex: 1,
    position: 'relative' as any,
  },
  webBackdrop: {
    position: 'absolute' as any,
    top: 180, // Header height
    left: 355, // Left pane width
    right: 355, // Right pane width
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1,
  },
  modalContentConstrained: {
    position: 'absolute' as any,
    top: 180, // Header height (matches webContentArea paddingTop)
    left: 355, // Left pane width
    right: 355, // Right pane width
    bottom: 0,
    backgroundColor: colors.background.surface,
    zIndex: 2,
    borderRadius: designTokens.borderRadius.lg,
    margin: spacing.lg,
    overflow: 'hidden',
    ...designTokens.shadows.lg,
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  closeButton: {
    padding: spacing.xs,
  },
  searchInput: {
    margin: spacing.lg,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    fontSize: 16,
    fontFamily: typography.fontFamily.regular,
  },
  countryList: {
    flex: 1,
  },
  countryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  countryFlag: {
    fontSize: 20,
  },
  countryInfo: {
    flex: 1,
  },
  countryName: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.textDark,
    marginBottom: 2,
    fontFamily: typography.fontFamily.medium,
  },
  countryDialCode: {
    fontSize: 14,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  inputCountryCode: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -8 }], // Center vertically
    zIndex: 2, // Higher z-index to ensure visibility
    fontSize: 16,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    minWidth: 30,
    marginRight: 8,
  },
});

PhoneInputComponent.displayName = 'PhoneInputComponent';
